import React, { useState, useEffect, useCallback, useRef } from "react";
import ManualEditsTable from "./ManualEditsTable";
import { SimpleDropdown } from "../../common/SimpleDropdown";
import { TeamDropdown } from "../../common/TeamDropdown";
import teamService from "../../../services/teamService";

const TEAMS_PER_PAGE = 15;

const ManualEditsSection = () => {
  const [roleFilter, setRoleFilter] = useState("player");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsLoadingMore, setTeamsLoadingMore] = useState(false);
  const [teamsHasMore, setTeamsHasMore] = useState(false);
  const teamsPageRef = useRef(1);
  const teamsSearchRef = useRef("");

  // Fetch teams with pagination
  // isInitial = true only on first mount, so search doesn't flash loading & close dropdown
  const fetchTeams = useCallback(async (page = 1, search = "", append = false, isInitial = false) => {
    if (isInitial) {
      setTeamsLoading(true);
    } else if (page > 1) {
      setTeamsLoadingMore(true);
    }
    try {
      const params = { page, limit: TEAMS_PER_PAGE };
      if (search) params.teamName = search;
      const data = await teamService.getPlayerTeams(params);
      const teamsList = data?.teams || data || [];
      const pagination = data?.pagination || {};
      const totalPages = pagination.totalPages || data?.totalPages || 1;
      const currentPage = pagination.currentPage || page;

      if (append) {
        setTeams((prev) => [...prev, ...teamsList]);
      } else {
        setTeams(teamsList);
      }
      teamsPageRef.current = currentPage;
      setTeamsHasMore(currentPage < totalPages);
    } catch (err) {
      console.error("Failed to fetch teams:", err);
    } finally {
      setTeamsLoading(false);
      setTeamsLoadingMore(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchTeams(1, "", false, true);
  }, [fetchTeams]);

  // API search handler (called by TeamDropdown with debounce)
  const handleTeamSearch = useCallback((searchTerm) => {
    teamsSearchRef.current = searchTerm;
    teamsPageRef.current = 1;
    fetchTeams(1, searchTerm, false);
  }, [fetchTeams]);

  // Load more handler (called by TeamDropdown on scroll)
  const handleTeamLoadMore = useCallback(() => {
    const nextPage = teamsPageRef.current + 1;
    fetchTeams(nextPage, teamsSearchRef.current, true);
  }, [fetchTeams]);

  // Debounce search input - wait 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  return (
    <div className="rounded-3xl  ">
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest">
              MANAGE PROFILES
            </h2>
            <p className="text-[11px] text-jp-muted mt-1">
              Coach/Scout/Player profiles
            </p>
          </div>

          <div className="flex items-center gap-3 text-sm text-jp-ink">
            <div className="flex items-center gap-2 h-8">
              <span>Team:</span>
              <TeamDropdown
                selectedTeam={selectedTeam}
                onSelectTeam={(team) => {
                  if (team) {
                    setSelectedTeam(team);
                    setTeamFilter(team._id || team.id || "");
                  } else {
                    setSelectedTeam(null);
                    setTeamFilter("");
                  }
                }}
                teams={teams}
                loading={teamsLoading}
                placeholder="All Teams"
                variant="admin"
                onSearch={handleTeamSearch}
                onLoadMore={handleTeamLoadMore}
                hasMore={teamsHasMore}
                loadingMore={teamsLoadingMore}
              />
            </div>
            <div className="flex items-center gap-2">
              <span>Role:</span>
              <SimpleDropdown
                value={roleFilter}
                onChange={(val) => setRoleFilter(val)}
                options={[
                  { value: "all", label: "All" },
                  { value: "player", label: "Player" },
                  { value: "scout", label: "Scout" },
                  { value: "coach", label: "Coach" },
                ]}
                placeholder="All"
                variant="admin"
              />
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-jp-muted"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-jp-section border border-jp-line text-sm text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:border-cyan-500/50 transition-colors"
          />
        </div>
      </div>

      <ManualEditsTable roleFilter={roleFilter} searchQuery={debouncedSearch} teamFilter={teamFilter} />
    </div>
  );
};

export default ManualEditsSection;
